import React from 'react';
import { Link } from 'react-router-dom';
import type { Listing } from '../domain/listings';
import { formatPrice, listingTypeLabel } from '../utils/listings';
import ListingStateBadge from './ListingStateBadge';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, Package } from 'lucide-react';

interface ListingCardProps {
  listing: Listing;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function ListingCard({ listing }: ListingCardProps) {
  const cover = listing.photos[0];
  const price = formatPrice(listing.price);

  return (
    <Link
      to={`/listings/${listing.id}`}
      className="group block overflow-hidden rounded-3xl bg-card transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-secondary"
    >
      {/* Editorial 4:5 crop with a subtle inner glow (DESIGN.md §5 Cards). */}
      <div className="relative aspect-[4/5] overflow-hidden">
        {cover ? (
          <img
            src={cover.url}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-muted text-muted-foreground">
            <Package className="size-12" />
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_40px_hsl(var(--foreground)/0.10)]" />
        <Badge className="absolute left-4 top-4 rounded-full border-transparent bg-background/80 text-xs font-semibold text-foreground backdrop-blur-md">
          {listingTypeLabel(listing.listingType)}
        </Badge>
        <ListingStateBadge listing={listing} className="absolute right-4 top-4" />
      </div>

      <div className="p-5">
        {/* Title and price separated by whitespace, not a divider (DESIGN.md §5) */}
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 className="line-clamp-1 text-lg font-bold leading-tight text-foreground">
            {listing.title}
          </h3>
          {price && (
            <span className="flex-shrink-0 text-lg font-extrabold text-primary">{price}</span>
          )}
        </div>

        <p className="mb-4 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
          {listing.description}
        </p>

        <div className="space-y-1 text-sm text-muted-foreground">
          {listing.location && (
            <div className="flex items-center">
              <MapPin className="mr-1.5 size-4" />
              <span>{listing.location}</span>
            </div>
          )}
          <div className="flex items-center">
            <Calendar className="mr-1.5 size-4" />
            <span>Posted {formatDate(listing.announcedAt ?? listing.createdAt)}</span>
          </div>
        </div>

        {listing.isExpired && (
          <div className="mt-3 flex items-center text-sm font-medium text-muted-foreground">
            <Clock className="mr-1.5 size-4" aria-hidden="true" />
            This listing has expired
          </div>
        )}
      </div>
    </Link>
  );
}
